import React, { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon,
  Shield,
  Bell,
  Palette,
  Database,
  Key,
  Globe,
  Mail,
  Smartphone,
  Lock,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  ExternalLink,
  Calendar,
  User,
  Server,
  FileText,
  Download,
  Upload,
  Trash2,
  Edit,
  Plus,
  Search,
  Filter,
  MoreVertical
} from 'lucide-react';
import {
  AdminGetGoogleAuthUrl,
  AdminHandleGoogleOAuth2Callback,
  AdminCheckGoogleOAuth2Status,
  GetAppVersion,
  UpdateAppVersion
} from '../components/Api.jsx';

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('integrations');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };
  const [oauthStatus, setOauthStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [authUrl, setAuthUrl] = useState('');
  const [callbackCode, setCallbackCode] = useState('');
  const [showPasswords, setShowPasswords] = useState({});
  const [settings, setSettings] = useState({
    general: {
      siteName: 'BYB Cult',
      siteDescription: 'Your fitness journey starts here',
      timezone: 'Asia/Kolkata',
      language: 'en',
      maintenanceMode: false
    },
    security: {
      twoFactorAuth: false,
      sessionTimeout: 30,
      passwordPolicy: 'strong',
      loginAttempts: 5,
      ipWhitelist: []
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      smsNotifications: false,
      adminAlerts: true,
      userAlerts: true
    },
    appearance: JSON.parse(localStorage.getItem('adminAppearance')) || {
      theme: 'light',
      primaryColor: '#3B82F6',
      secondaryColor: '#6B7280',
      logo: '',
      favicon: ''
    },
    integrations: {
      googleOAuth: false,
      facebookLogin: false,
      twitterLogin: false,
      paymentGateway: 'razorpay',
      analytics: 'google'
    },
    backup: {
      autoBackup: true,
      backupFrequency: 'daily',
      retentionDays: 30,
      cloudStorage: false
    },
    appVersion: {
      android: {
        latestVersion: '',
        minSupportedVersion: '',
        mandatory: false,
        updateMessage: '',
        updateUrl: '',
        isActive: true,
        buildNumber: ''
      },
      ios: {
        latestVersion: '',
        minSupportedVersion: '',
        mandatory: false,
        updateMessage: '',
        updateUrl: '',
        isActive: true,
        buildNumber: ''
      }
    }
  });

  const tabs = [
    { id: 'general', label: 'General', icon: SettingsIcon, hidden: true },
    { id: 'security', label: 'Security', icon: Shield, hidden: true },
    { id: 'notifications', label: 'Notifications', icon: Bell, hidden: true },
    { id: 'appearance', label: 'Appearance', icon: Palette, hidden: true },
    { id: 'integrations', label: 'Integrations', icon: Globe, hidden: false },
    { id: 'backup', label: 'Backup & Restore', icon: Database, hidden: true },
    { id: 'appVersion', label: 'App Versioning', icon: Smartphone, hidden: false },
    { id: 'system', label: 'System Info', icon: Server, hidden: true }
  ];

  useEffect(() => {
    if (activeTab === 'integrations') {
      checkOAuth2Status();
    }
    if (activeTab === 'appVersion') {
      fetchAppVersions();
    }
  }, [activeTab]);

  // Apply appearance settings to document and save to local storage
  useEffect(() => {
    const { theme, primaryColor } = settings.appearance;

    // Save to local storage
    localStorage.setItem('adminAppearance', JSON.stringify(settings.appearance));

    // Apply Theme
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else if (theme === 'auto') {
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }

    // Apply Primary Color as a CSS variable
    document.documentElement.style.setProperty('--primary-color', primaryColor);

  }, [settings.appearance]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showToast('Logo file size must be less than 10MB', 'error');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        handleSettingChange('appearance', 'logo', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const fetchAppVersions = async () => {
    setLoading(true);
    try {
      const [androidRes, iosRes] = await Promise.all([
        GetAppVersion('android'),
        GetAppVersion('ios')
      ]);

      setSettings(prev => ({
        ...prev,
        appVersion: {
          android: androidRes.data.data || prev.appVersion.android,
          ios: iosRes.data.data || prev.appVersion.ios
        }
      }));
    } catch (error) {
      console.error('Error fetching app versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkOAuth2Status = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await AdminCheckGoogleOAuth2Status();
      setOauthStatus(response.data);
    } catch (error) {
      console.error('Error checking OAuth2 status:', error);
      setOauthStatus({
        status: 'error',
        message: 'Error checking OAuth2 status: ' + error.message
      });
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    checkOAuth2Status(true);
  };

  const getGoogleAuthUrl = async () => {
    setLoading(true);
    try {
      const response = await AdminGetGoogleAuthUrl();
      setAuthUrl(response.data.authUrl);
    } catch (error) {
      console.error('Error getting auth URL:', error);
      showToast('Error getting Google authorization URL: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth2Callback = async () => {
    if (!callbackCode.trim()) {
      showToast('Please enter the authorization code', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await AdminHandleGoogleOAuth2Callback(callbackCode);
      showToast('🎉 Google OAuth2 authentication successful!', 'success');
      setCallbackCode('');
      await checkOAuth2Status();
    } catch (error) {
      console.error('Error handling OAuth2 callback:', error);
      showToast('Error handling OAuth2 callback: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard!', 'success');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'configured':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'not_configured':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'expired':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'configured':
        return 'bg-green-100 text-green-800';
      case 'not_configured':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const saveSettings = async (category) => {
    setLoading(true);
    try {
      if (category === 'appVersion') {
        // App version needs two separate calls or we just save the active one
        // For simplicity, we save the one that has data or we could save both
        await Promise.all([
          UpdateAppVersion({ ...settings.appVersion.android, platform: 'android' }),
          UpdateAppVersion({ ...settings.appVersion.ios, platform: 'ios' })
        ]);
      }

      // Here you would typically make an API call to save other settings
      console.log(`Saving ${category} settings:`, settings[category]);
      showToast(`${category.charAt(0).toUpperCase() + category.slice(1)} settings saved successfully!`, 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('Error saving settings: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
          <input
            type="text"
            value={settings.general.siteName}
            onChange={(e) => handleSettingChange('general', 'siteName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-500 font-medium"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
          <select
            value={settings.general.timezone}
            onChange={(e) => handleSettingChange('general', 'timezone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-500 font-medium"
          >
            <option value="Asia/Kolkata">Asia/Kolkata</option>
            <option value="UTC">UTC</option>
            <option value="America/New_York">America/New_York</option>
            <option value="Europe/London">Europe/London</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Site Description</label>
        <textarea
          value={settings.general.siteDescription}
          onChange={(e) => handleSettingChange('general', 'siteDescription', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-500 font-medium"
        />
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="maintenanceMode"
          checked={settings.general.maintenanceMode}
          onChange={(e) => handleSettingChange('general', 'maintenanceMode', e.target.checked)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="maintenanceMode" className="ml-2 block text-sm text-gray-700">
          Enable Maintenance Mode
        </label>
      </div>

      <button
        onClick={() => saveSettings('general')}
        disabled={loading}
        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
      >
        <Save className="w-4 h-4" />
        Save General Settings
      </button>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
          <input
            type="number"
            value={settings.security.sessionTimeout}
            onChange={(e) => handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-500 font-medium"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Max Login Attempts</label>
          <input
            type="number"
            value={settings.security.loginAttempts}
            onChange={(e) => handleSettingChange('security', 'loginAttempts', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-500 font-medium"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Password Policy</label>
        <select
          value={settings.security.passwordPolicy}
          onChange={(e) => handleSettingChange('security', 'passwordPolicy', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-500 font-medium"
        >
          <option value="basic">Basic (6+ characters)</option>
          <option value="medium">Medium (8+ chars, 1 number)</option>
          <option value="strong">Strong (8+ chars, 1 number, 1 special)</option>
        </select>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="twoFactorAuth"
            checked={settings.security.twoFactorAuth}
            onChange={(e) => handleSettingChange('security', 'twoFactorAuth', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="twoFactorAuth" className="ml-2 block text-sm text-gray-700">
            Enable Two-Factor Authentication
          </label>
        </div>
      </div>

      <button
        onClick={() => saveSettings('security')}
        disabled={loading}
        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
      >
        <Save className="w-4 h-4" />
        Save Security Settings
      </button>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Email Notifications</h3>
            <p className="text-sm text-gray-500">Send notifications via email</p>
          </div>
          <input
            type="checkbox"
            checked={settings.notifications.emailNotifications}
            onChange={(e) => handleSettingChange('notifications', 'emailNotifications', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Push Notifications</h3>
            <p className="text-sm text-gray-500">Send push notifications to mobile devices</p>
          </div>
          <input
            type="checkbox"
            checked={settings.notifications.pushNotifications}
            onChange={(e) => handleSettingChange('notifications', 'pushNotifications', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">SMS Notifications</h3>
            <p className="text-sm text-gray-500">Send notifications via SMS</p>
          </div>
          <input
            type="checkbox"
            checked={settings.notifications.smsNotifications}
            onChange={(e) => handleSettingChange('notifications', 'smsNotifications', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Admin Alerts</h3>
            <p className="text-sm text-gray-500">Send alerts to administrators</p>
          </div>
          <input
            type="checkbox"
            checked={settings.notifications.adminAlerts}
            onChange={(e) => handleSettingChange('notifications', 'adminAlerts', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>
      </div>

      <button
        onClick={() => saveSettings('notifications')}
        disabled={loading}
        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
      >
        <Save className="w-4 h-4" />
        Save Notification Settings
      </button>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
          <select
            value={settings.appearance.theme}
            onChange={(e) => handleSettingChange('appearance', 'theme', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-500 font-medium"
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
            <option value="auto">Auto</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
          <input
            type="color"
            value={settings.appearance.primaryColor}
            onChange={(e) => handleSettingChange('appearance', 'primaryColor', e.target.value)}
            className="w-full h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Logo Upload</label>

        {settings.appearance.logo ? (
          <div className="mt-1 border border-gray-300 rounded-lg p-4 bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded border bg-white flex items-center justify-center overflow-hidden">
                <img src={settings.appearance.logo} alt="Logo preview" className="max-w-full max-h-full object-contain" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Custom Logo Applied</p>
                <p className="text-xs text-gray-500">This logo is saved locally.</p>
              </div>
            </div>
            <button
              onClick={() => handleSettingChange('appearance', 'logo', '')}
              className="text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 p-2 rounded-lg transition-colors"
              title="Remove Logo"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 transition-colors">
            <div className="space-y-1 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
              <div className="flex text-sm text-gray-600 justify-center">
                <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                  <span>Click to upload</span>
                  <input
                    type="file"
                    className="sr-only"
                    accept="image/png, image/jpeg, image/gif, image/svg+xml"
                    onChange={handleLogoUpload}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={() => saveSettings('appearance')}
        disabled={loading}
        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
      >
        <Save className="w-4 h-4" />
        Save Appearance Settings
      </button>
    </div>
  );

  const renderIntegrationSettings = () => (
    <div className="space-y-6">
      {/* Google OAuth 2 Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Calendar className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Google OAuth 2</h3>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-600">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span>Checking OAuth2 status...</span>
          </div>
        ) : oauthStatus ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              {getStatusIcon(oauthStatus.status)}
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(oauthStatus.status)}`}>
                {oauthStatus.status?.toUpperCase() || 'UNKNOWN'}
              </span>
            </div>

            <p className="text-gray-700">{oauthStatus.message}</p>

            {oauthStatus.hasValidToken && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-800 mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">Token Details</span>
                </div>
                <div className="text-sm text-green-700 space-y-1">
                  <p>Created: {oauthStatus.tokenCreatedAt ? new Date(oauthStatus.tokenCreatedAt).toLocaleString() : 'N/A'}</p>
                  <p>Expires in: {oauthStatus.expiresIn ? `${Math.floor(oauthStatus.expiresIn / 60)} minutes` : 'N/A'}</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-500">No status information available</p>
        )}

        {oauthStatus && oauthStatus.status !== 'configured' && (
          <div className="mt-6 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Setup Google OAuth2</h4>
              <p className="text-sm text-blue-800 mb-3">
                Click the button below to generate a Google OAuth2 authorization URL. This will open Google's authorization page in a new tab.
              </p>
              <button
                onClick={getGoogleAuthUrl}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                {loading ? 'Generating URL...' : 'Get Authorization URL'}
              </button>
            </div>

            {authUrl && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-900 mb-2">Complete Google Authorization</h4>
                <p className="text-sm text-green-800 mb-3">
                  A new tab should have opened with Google's authorization page. Complete the authorization process there.
                </p>
                <div className="flex items-center gap-2 mb-3">
                  <a
                    href={authUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Authorization URL
                  </a>
                  <button
                    onClick={() => copyToClipboard(authUrl)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm"
                  >
                    <Copy className="w-4 h-4" />
                    Copy URL
                  </button>
                </div>
                <div className="bg-white border border-gray-300 rounded p-2 text-xs text-gray-600 break-all">
                  {authUrl}
                </div>
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-900 mb-2">Enter Authorization Code</h4>
              <p className="text-sm text-yellow-800 mb-3">
                After completing the Google authorization, you should have received an authorization code. Enter it below to complete the OAuth2 setup.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={callbackCode}
                  onChange={(e) => setCallbackCode(e.target.value)}
                  placeholder="Enter authorization code here..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-500"
                />
                <button
                  onClick={handleOAuth2Callback}
                  disabled={loading || !callbackCode.trim()}
                  className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm"
                >
                  <CheckCircle className="w-4 h-4" />
                  {loading ? 'Processing...' : 'Complete Setup'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Other Integrations */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Facebook Login</h3>
            <p className="text-sm text-gray-500">Allow users to login with Facebook</p>
          </div>
          <input
            type="checkbox"
            checked={settings.integrations.facebookLogin}
            onChange={(e) => handleSettingChange('integrations', 'facebookLogin', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Twitter Login</h3>
            <p className="text-sm text-gray-500">Allow users to login with Twitter</p>
          </div>
          <input
            type="checkbox"
            checked={settings.integrations.twitterLogin}
            onChange={(e) => handleSettingChange('integrations', 'twitterLogin', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Payment Gateway</label>
          <select
            value={settings.integrations.paymentGateway}
            onChange={(e) => handleSettingChange('integrations', 'paymentGateway', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-500 font-medium"
          >
            <option value="razorpay">Razorpay</option>
            <option value="stripe">Stripe</option>
            <option value="paypal">PayPal</option>
          </select>
        </div>
      </div>

      <button
        onClick={() => saveSettings('integrations')}
        disabled={loading}
        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
      >
        <Save className="w-4 h-4" />
        Save Integration Settings
      </button>
    </div>
  );

  const renderBackupSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Backup Frequency</label>
          <select
            value={settings.backup.backupFrequency}
            onChange={(e) => handleSettingChange('backup', 'backupFrequency', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-500 font-medium"
          >
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Retention Days</label>
          <input
            type="number"
            value={settings.backup.retentionDays}
            onChange={(e) => handleSettingChange('backup', 'retentionDays', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-500 font-medium"
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Auto Backup</h3>
            <p className="text-sm text-gray-500">Automatically backup data</p>
          </div>
          <input
            type="checkbox"
            checked={settings.backup.autoBackup}
            onChange={(e) => handleSettingChange('backup', 'autoBackup', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Cloud Storage</h3>
            <p className="text-sm text-gray-500">Store backups in cloud storage</p>
          </div>
          <input
            type="checkbox"
            checked={settings.backup.cloudStorage}
            onChange={(e) => handleSettingChange('backup', 'cloudStorage', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>
      </div>

      <div className="flex gap-4">
        <button
          onClick={() => saveSettings('backup')}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Save className="w-4 h-4" />
          Save Backup Settings
        </button>
        <button
          onClick={() => alert('Backup started!')}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Download className="w-4 h-4" />
          Create Backup Now
        </button>
      </div>
    </div>
  );

  const renderAppVersionSettings = () => {
    const handleVersionChange = (platform, key, value) => {
      setSettings(prev => ({
        ...prev,
        appVersion: {
          ...prev.appVersion,
          [platform]: {
            ...prev.appVersion[platform],
            [key]: value
          }
        }
      }));
    };

    const renderPlatformFields = (platform) => (
      <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2 capitalize">
          {platform === 'android' ? <Smartphone className="w-5 h-5 text-green-600" /> : <Smartphone className="w-5 h-5 text-gray-600" />}
          {platform} Version Settings
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Latest Version</label>
            <input
              type="text"
              placeholder="e.g. 1.0.5"
              value={settings.appVersion[platform].latestVersion}
              onChange={(e) => handleVersionChange(platform, 'latestVersion', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Build Number</label>
            <input
              type="number"
              placeholder="e.g. 18"
              value={settings.appVersion[platform].buildNumber}
              onChange={(e) => handleVersionChange(platform, 'buildNumber', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min Supported Version</label>
            <input
              type="text"
              placeholder="e.g. 1.0.0"
              value={settings.appVersion[platform].minSupportedVersion}
              onChange={(e) => handleVersionChange(platform, 'minSupportedVersion', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Update URL</label>
            <input
              type="text"
              placeholder="Store link..."
              value={settings.appVersion[platform].updateUrl}
              onChange={(e) => handleVersionChange(platform, 'updateUrl', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Update Message</label>
          <textarea
            rows={2}
            placeholder="Message to users..."
            value={settings.appVersion[platform].updateMessage}
            onChange={(e) => handleVersionChange(platform, 'updateMessage', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
          />
        </div>

        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.appVersion[platform].mandatory}
              onChange={(e) => handleVersionChange(platform, 'mandatory', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700 font-medium">Mandatory Update</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.appVersion[platform].isActive}
              onChange={(e) => handleVersionChange(platform, 'isActive', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700 font-medium">Is Active</span>
          </label>
        </div>
      </div>
    );

    return (
      <div className="space-y-8">
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
          <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Active App Versions (Publicly Visible)
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-xs">
              <span className="text-gray-600">Android:</span>
              <span className="ml-2 font-bold text-gray-900 bg-white px-2 py-0.5 rounded border border-gray-200">
                {settings.appVersion.android.latestVersion || 'N/A'} (Build: {settings.appVersion.android.buildNumber || '0'})
              </span>
            </div>
            <div className="text-xs">
              <span className="text-gray-600">iOS:</span>
              <span className="ml-2 font-bold text-gray-900 bg-white px-2 py-0.5 rounded border border-gray-200">
                {settings.appVersion.ios.latestVersion || 'N/A'} (Build: {settings.appVersion.ios.buildNumber || '0'})
              </span>
            </div>
          </div>
        </div>
        {renderPlatformFields('android')}
        {renderPlatformFields('ios')}

        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={() => saveSettings('appVersion')}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 transition-all font-medium shadow-sm"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save & Publish All Versions
          </button>
        </div>
      </div>
    );
  };

  const renderSystemInfo = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">System Information</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex justify-between">
              <span>Version:</span>
              <span>v1.0.0</span>
            </div>
            <div className="flex justify-between">
              <span>Environment:</span>
              <span>Production</span>
            </div>
            <div className="flex justify-between">
              <span>Database:</span>
              <span>MySQL 8.0</span>
            </div>
            <div className="flex justify-between">
              <span>Last Updated:</span>
              <span>2024-01-15</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Server Status</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex justify-between">
              <span>CPU Usage:</span>
              <span className="text-green-600">45%</span>
            </div>
            <div className="flex justify-between">
              <span>Memory:</span>
              <span className="text-green-600">2.1GB / 8GB</span>
            </div>
            <div className="flex justify-between">
              <span>Disk Space:</span>
              <span className="text-yellow-600">75%</span>
            </div>
            <div className="flex justify-between">
              <span>Uptime:</span>
              <span>15 days</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Recent Logs</h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-gray-600">2024-01-15 10:30:00 - User login successful</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-gray-600">2024-01-15 10:25:00 - Database backup completed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span className="text-gray-600">2024-01-15 10:20:00 - High memory usage warning</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings();
      case 'security':
        return renderSecuritySettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'appearance':
        return renderAppearanceSettings();
      case 'integrations':
        return renderIntegrationSettings();
      case 'backup':
        return renderBackupSettings();
      case 'appVersion':
        return renderAppVersionSettings();
      case 'system':
        return renderSystemInfo();
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <SettingsIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    Manage your application settings and configurations. Configure system parameters, security settings, notifications, and other administrative preferences.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh settings"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        {/* Horizontal Tab Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 overflow-hidden">
          <nav className="flex space-x-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {tabs.filter(tab => !tab.hidden).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors whitespace-nowrap ${activeTab === tab.id
                  ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="w-full bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {tabs.find(tab => tab.id === activeTab)?.label}
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              {activeTab === 'general' && 'Configure general application settings'}
              {activeTab === 'security' && 'Manage security and authentication settings'}
              {activeTab === 'notifications' && 'Configure notification preferences'}
              {activeTab === 'appearance' && 'Customize the look and feel of your application'}
              {activeTab === 'integrations' && 'Manage third-party integrations and APIs'}
              {activeTab === 'backup' && 'Configure backup and restore settings'}
              {activeTab === 'appVersion' && 'Manage mobile app versions and mandatory update requirements'}
              {activeTab === 'system' && 'View system information and status'}
            </p>
          </div>

          {renderTabContent()}
        </div>
      </div>

      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-[9999] p-4 rounded-lg shadow-lg border ${toast.type === 'error' ? 'bg-red-500 text-white border-red-600' : 'bg-green-500 text-white border-green-600'
          }`}>
          <div className="flex items-center gap-3">
            {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-gray-500 text-sm">Powered by TCY Consulting</p>
      </div>
    </div>
  );
}
